/**
 * Style Space feature flags.
 *
 * `STYLE_SPACE_AI_ENABLED` — AIRA promises the backend cannot keep yet
 * (capsule refine tip/drawer, B5/B6). Off until `docs/PLAYGROUND_ML_CONTRACTS.md`
 * routes land; the local look generation stays on regardless.
 * `STYLE_SPACE_CALENDAR_ENABLED` — the calendar strip (B13, out of scope v1).
 * `STYLE_SPACE_ML_CLIENT_ENABLED` — route the local look logic to `api/ml.ts`.
 * Shuffle / Replace are the stylist's actions (`POST /aira/actions`, B2) since 2026-09-13 —
 * no local re-deal exists any more: a stylist, not a randomiser.
 */
export const STYLE_SPACE_AI_ENABLED = import.meta.env.VITE_FEATURE_STYLE_SPACE_AI === 'true'
export const STYLE_SPACE_CALENDAR_ENABLED =
  import.meta.env.VITE_FEATURE_STYLE_SPACE_CALENDAR === 'true'
export const STYLE_SPACE_ML_CLIENT_ENABLED =
  import.meta.env.VITE_FEATURE_STYLE_SPACE_ML === 'true'

/**
 * Style Space v2 (redesign) backend capabilities — `docs/backend/STYLE_SPACE_V2_API_GAPS.md`.
 * Each id maps to one endpoint / field the redesign needs. Off → the UI runs its mock or
 * client-side fallback; on → the real call. Enable per capability as the backend ships:
 *   VITE_STYLE_SPACE_V2_API="style_dna,wardrobe_facets,link_item"   (or "all")
 */
export const STYLE_SPACE_V2_API_CAPABILITIES = [
  /** §1 `GET /playground/user-wardrobe` — colour / season filters + facet counts */
  'wardrobe_facets',
  /** §2 `GET /playground/style-dna` */
  'style_dna',
  /** §3 `POST /playground/user-wardrobe/{id}/link` */
  'link_item',
  /** §5 `PUT /playground/outfits/{id}/favorite` */
  'outfit_favorite',
  /** §6 `POST /playground/outfits/{id}/duplicate` */
  'outfit_duplicate',
  /** §8 `GET /ml-classification/subcategories/search` */
  'subcategory_search',
  /** §10 `style_ids[]` on the wardrobe PATCH / detail */
  'style_ids',
  /** §11 `item_source` on the stylist's envelope — wardrobe / mixed / catalog */
  'playground_item_source',
  /** §12 `GET /playground/outfits?favorite=1` pieces as a palette source */
  'playground_favourites_palette',
  /** §13 `keep_slots[]` on `POST /aira/actions` — pinned pieces survive a shuffle */
  'playground_keep_slots',
] as const

export type StyleSpaceV2ApiCapability = (typeof STYLE_SPACE_V2_API_CAPABILITIES)[number]

function parseStyleSpaceV2ApiEnv(raw: string | undefined): ReadonlySet<StyleSpaceV2ApiCapability> {
  const tokens = (raw ?? '')
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean)
  if (tokens.includes('all')) return new Set(STYLE_SPACE_V2_API_CAPABILITIES)
  return new Set(
    tokens.filter((token): token is StyleSpaceV2ApiCapability =>
      (STYLE_SPACE_V2_API_CAPABILITIES as readonly string[]).includes(token),
    ),
  )
}

/**
 * QA / integration override without a rebuild (dev + preview builds only):
 *   localStorage.setItem('fair:style-space-v2-api', 'style_dna,link_item')  → reload
 */
export const STYLE_SPACE_V2_API_STORAGE_KEY = 'fair:style-space-v2-api'

function readStyleSpaceV2ApiOverride(): string | undefined {
  if (!import.meta.env.DEV || typeof window === 'undefined') return undefined
  try {
    return window.localStorage.getItem(STYLE_SPACE_V2_API_STORAGE_KEY) ?? undefined
  } catch {
    return undefined
  }
}

const STYLE_SPACE_V2_API_ENABLED = parseStyleSpaceV2ApiEnv(
  readStyleSpaceV2ApiOverride() ?? import.meta.env.VITE_STYLE_SPACE_V2_API,
)

export function isStyleSpaceV2ApiEnabled(capability: StyleSpaceV2ApiCapability): boolean {
  return STYLE_SPACE_V2_API_ENABLED.has(capability)
}
